import type { ComponentType } from "react";
import type {
  DetailComponentProps,
  ListComponentProps
} from "./MasterDetailLayout";
import { AppointmentItemDetail } from "./sections/appointments/AppointmentItemDetail";
import { AppointmentItemList } from "./sections/appointments/AppointmentItemList";
import { ContactItemDetail } from "./sections/contacts/ContactItemDetail";
import { ContactItemList } from "./sections/contacts/ContactItemList";
import { EmailItemDetail } from "./sections/email/EmailItemDetail";
import { EmailItemList } from "./sections/email/EmailItemList";
import { LeadItemDetail } from "./sections/leads/LeadItemDetail";
import { LeadItemList } from "./sections/leads/LeadItemList";
import { PhoneCallItemDetail } from "./sections/phoneCalls/PhoneCallItemDetail";
import { PhoneCallItemList } from "./sections/phoneCalls/PhoneCallItemList";
import { AdvertisingSourcesPage } from "./sections/single/AdvertisingSources";
import { AutoRespondersPage } from "./sections/single/AutoResponders";
import { ContactsPage } from "./sections/single/Contacts";
import { LeadDistributionPage } from "./sections/single/LeadDistribution";
import { LeadLifecyclesPage } from "./sections/single/LeadLifecycles";
import { TaskItemDetail } from "./sections/tasks/TaskItemDetail";
import { TaskItemList } from "./sections/tasks/TaskItemList";
import { WebFormItemDetail } from "./sections/webForms/WebFormItemDetail";
import { WebFormItemList } from "./sections/webForms/WebFormItemList";
import type { SectionKey } from "./hashConfig";

interface MasterDetailConfig {
  layout: "master-detail";
  DetailComponent: ComponentType<DetailComponentProps>;
  ListComponent: ComponentType<ListComponentProps>;
  label: string;
}

interface SingleConfig {
  layout: "single";
  Component: ComponentType;
  label: string;
}

export type SectionConfig = MasterDetailConfig | SingleConfig;

export const isMasterDetail = (c: SectionConfig): c is MasterDetailConfig =>
  c.layout === "master-detail";

export const SECTION_CONFIG: Record<SectionKey, SectionConfig> = {
  active: {
    layout: "master-detail",
    DetailComponent: ContactItemDetail,
    ListComponent: ContactItemList,
    label: "Active"
  },
  advertisingsources: {
    layout: "single",
    Component: AdvertisingSourcesPage,
    label: "Advertising Sources"
  },
  all: {
    layout: "master-detail",
    DetailComponent: LeadItemDetail,
    ListComponent: LeadItemList,
    label: "All Leads"
  },
  appointments: {
    layout: "master-detail",
    DetailComponent: AppointmentItemDetail,
    ListComponent: AppointmentItemList,
    label: "Appointments"
  },
  autoresponders: {
    layout: "single",
    Component: AutoRespondersPage,
    label: "Auto Responders"
  },
  bounced: {
    layout: "master-detail",
    DetailComponent: EmailItemDetail,
    ListComponent: EmailItemList,
    label: "Bounced"
  },
  contacts: { layout: "single", Component: ContactsPage, label: "Contacts" },
  deleted: {
    layout: "master-detail",
    DetailComponent: ContactItemDetail,
    ListComponent: ContactItemList,
    label: "Deleted"
  },
  leaddistribution: {
    layout: "single",
    Component: LeadDistributionPage,
    label: "Lead Distribution"
  },
  leadlifecycles: {
    layout: "single",
    Component: LeadLifecyclesPage,
    label: "Lead Lifecycles"
  },
  phonecalls: {
    layout: "master-detail",
    DetailComponent: PhoneCallItemDetail,
    ListComponent: PhoneCallItemList,
    label: "Phone Calls"
  },
  recent: {
    layout: "master-detail",
    DetailComponent: LeadItemDetail,
    ListComponent: LeadItemList,
    label: "Recent"
  },
  replies: {
    layout: "master-detail",
    DetailComponent: EmailItemDetail,
    ListComponent: EmailItemList,
    label: "Email Replies"
  },
  spam: {
    layout: "master-detail",
    DetailComponent: EmailItemDetail,
    ListComponent: EmailItemList,
    label: "Spam"
  },
  tasks: {
    layout: "master-detail",
    DetailComponent: TaskItemDetail,
    ListComponent: TaskItemList,
    label: "Tasks"
  },
  thirdparty: {
    layout: "master-detail",
    DetailComponent: LeadItemDetail,
    ListComponent: LeadItemList,
    label: "3rd Party"
  },
  unmatched: {
    layout: "master-detail",
    DetailComponent: EmailItemDetail,
    ListComponent: EmailItemList,
    label: "Unmatched"
  },
  walkin: {
    layout: "master-detail",
    DetailComponent: LeadItemDetail,
    ListComponent: LeadItemList,
    label: "Walk In"
  },
  websiteforms: {
    layout: "master-detail",
    DetailComponent: WebFormItemDetail,
    ListComponent: WebFormItemList,
    label: "Website Forms"
  }
};
